/**
 * Roadmaps Service
 *
 * API endpoints for roadmap operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type {
  Roadmap,
  RoadmapWithCompany,
  RoadmapFull,
  RoadmapTheme,
  RoadmapThemeWithRoadmap,
  RoadmapItemWithDetails,
  RoadmapItemComment,
  RoadmapItemCommentWithReplies,
  RoadmapItemHistory,
} from '@medsuite/models';

// Create/Update DTOs
export interface CreateRoadmapDto {
  company_id: number;
  roadmap_title: string;
  roadmap_context?: string | null;
}

export interface UpdateRoadmapDto {
  company_id?: number;
  roadmap_title?: string;
  roadmap_context?: string | null;
}

export interface CreateRoadmapThemeDto {
  roadmap_id: number;
  theme_title: string;
  theme_context?: string | null;
  theme_sequence?: number;
}

export interface UpdateRoadmapThemeDto {
  roadmap_id?: number;
  theme_title?: string;
  theme_context?: string | null;
  theme_sequence?: number;
}

export interface CreateRoadmapItemDto {
  roadmap_theme_id: number;
  investment_id: number;
  start_date: string;
  end_date: string;
  is_committed?: boolean;
  committed_date?: string;
  delivery_date?: string | null;
  tolerance_days?: number;
  tolerance_percent?: number;
  estimated_effort_days?: number;
  estimated_remaining_days?: number;
  top_priority_flag?: boolean;
  baseline_date?: string | null;
  baseline_start_date?: string | null;
  baseline_end_date?: string | null;
}

export interface UpdateRoadmapItemDto {
  roadmap_theme_id?: number;
  investment_id?: number;
  start_date?: string;
  end_date?: string;
  is_committed?: boolean;
  committed_date?: string;
  delivery_date?: string | null;
  tolerance_days?: number;
  tolerance_percent?: number;
  estimated_effort_days?: number;
  estimated_remaining_days?: number;
  top_priority_flag?: boolean;
  baseline_date?: string | null;
  baseline_start_date?: string | null;
  baseline_end_date?: string | null;
}

export interface CreateRoadmapItemCommentDto {
  roadmap_item_id: number;
  comment: string;
  comment_by_name: string;
  comment_date?: string;
  comment_by_email: string;
  parent_comment_id?: number | null;
}

export interface UpdateRoadmapItemCommentDto {
  comment?: string;
  comment_by_name?: string;
  comment_date?: string;
  comment_by_email?: string;
}

/**
 * Roadmaps Service
 */
class RoadmapsService {
  async getAll(): Promise<ApiResponse<RoadmapWithCompany[]>> {
    return apiClient.get<RoadmapWithCompany[]>('/admin/roadmaps');
  }

  async getById(id: number): Promise<ApiResponse<RoadmapWithCompany>> {
    return apiClient.get<RoadmapWithCompany>(`/admin/roadmaps/${id}`);
  }

  async getFull(id: number): Promise<ApiResponse<RoadmapFull>> {
    return apiClient.get<RoadmapFull>(`/admin/roadmaps/${id}/full`);
  }

  async create(data: CreateRoadmapDto): Promise<ApiResponse<Roadmap>> {
    return apiClient.post<Roadmap>('/admin/roadmaps', data);
  }

  async update(id: number, data: UpdateRoadmapDto): Promise<ApiResponse<Roadmap>> {
    return apiClient.put<Roadmap>(`/admin/roadmaps/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/roadmaps/${id}`);
  }
}

/**
 * Roadmap Themes Service
 */
class RoadmapThemesService {
  async getAll(
    roadmapId?: number
  ): Promise<ApiResponse<RoadmapTheme[] | RoadmapThemeWithRoadmap[]>> {
    const params = roadmapId ? { roadmap_id: roadmapId } : undefined;
    return apiClient.get<RoadmapTheme[] | RoadmapThemeWithRoadmap[]>('/admin/roadmap-themes', {
      params,
    });
  }

  async getById(id: number): Promise<ApiResponse<RoadmapTheme>> {
    return apiClient.get<RoadmapTheme>(`/admin/roadmap-themes/${id}`);
  }

  async create(data: CreateRoadmapThemeDto): Promise<ApiResponse<RoadmapTheme>> {
    return apiClient.post<RoadmapTheme>('/admin/roadmap-themes', data);
  }

  async update(id: number, data: UpdateRoadmapThemeDto): Promise<ApiResponse<RoadmapTheme>> {
    return apiClient.put<RoadmapTheme>(`/admin/roadmap-themes/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/roadmap-themes/${id}`);
  }

  async reorder(id: number, direction: 'up' | 'down'): Promise<ApiResponse<RoadmapTheme[]>> {
    return apiClient.post<RoadmapTheme[]>(`/admin/roadmap-themes/${id}/reorder`, { direction });
  }
}

/**
 * Roadmap Items Service
 */
class RoadmapItemsService {
  async getAll(params?: {
    roadmap_id?: number;
    theme_id?: number;
    investment_id?: number;
  }): Promise<ApiResponse<RoadmapItemWithDetails[]>> {
    return apiClient.get<RoadmapItemWithDetails[]>('/admin/roadmap-items', { params });
  }

  async getById(id: number): Promise<ApiResponse<RoadmapItemWithDetails>> {
    return apiClient.get<RoadmapItemWithDetails>(`/admin/roadmap-items/${id}`);
  }

  async create(data: CreateRoadmapItemDto): Promise<ApiResponse<RoadmapItemWithDetails>> {
    return apiClient.post<RoadmapItemWithDetails>('/admin/roadmap-items', data);
  }

  async update(
    id: number,
    data: UpdateRoadmapItemDto
  ): Promise<ApiResponse<RoadmapItemWithDetails>> {
    return apiClient.put<RoadmapItemWithDetails>(`/admin/roadmap-items/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/roadmap-items/${id}`);
  }

  async getHistory(id: number, since?: string): Promise<ApiResponse<RoadmapItemHistory[]>> {
    const params = since ? { since } : undefined;
    return apiClient.get<RoadmapItemHistory[]>(`/admin/roadmap-items/${id}/history`, { params });
  }

  async setBaseline(id: number): Promise<ApiResponse<RoadmapItemWithDetails>> {
    return apiClient.post<RoadmapItemWithDetails>(`/admin/roadmap-items/${id}/baseline`);
  }
}

/**
 * Roadmap Item Comments Service
 */
class RoadmapItemCommentsService {
  async getAll(
    itemId: number,
    threaded = false
  ): Promise<ApiResponse<RoadmapItemComment[] | RoadmapItemCommentWithReplies[]>> {
    return apiClient.get<RoadmapItemComment[] | RoadmapItemCommentWithReplies[]>(
      '/admin/roadmap-item-comments',
      {
        params: { roadmap_item_id: itemId, threaded },
      }
    );
  }

  async getById(id: number): Promise<ApiResponse<RoadmapItemComment>> {
    return apiClient.get<RoadmapItemComment>(`/admin/roadmap-item-comments/${id}`);
  }

  async create(data: CreateRoadmapItemCommentDto): Promise<ApiResponse<RoadmapItemComment>> {
    return apiClient.post<RoadmapItemComment>('/admin/roadmap-item-comments', data);
  }

  async update(
    id: number,
    data: UpdateRoadmapItemCommentDto
  ): Promise<ApiResponse<RoadmapItemComment>> {
    return apiClient.put<RoadmapItemComment>(`/admin/roadmap-item-comments/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/roadmap-item-comments/${id}`);
  }
}

// Export service instances
export const roadmapsService = new RoadmapsService();
export const roadmapThemesService = new RoadmapThemesService();
export const roadmapItemsService = new RoadmapItemsService();
export const roadmapItemCommentsService = new RoadmapItemCommentsService();
